import type React from 'react'
import {createContext, use, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useIsCursorEnabled} from '../hooks/use-cursor-navigation'
import {useSearchTerm, useSetSearchTerm} from './SearchTermContext'

/**
 * Context for whether the find-in-file panel is open.
 * Split from the dispatch context so components that only toggle the panel don't re-render when it opens or closes.
 */
const FindInFileOpenStateContext = createContext<boolean>(false)

/**
 * Context for the find-in-file toggle function.
 * The setter is referentially stable, so dispatch-only consumers never re-render — notably not on every keystroke
 * while typing a search term.
 */
const FindInFileOpenDispatchContext = createContext<(open: boolean) => void>(() => undefined)

/**
 * This is equivalent to a simple setState, except we save the search state of the find in file panel when it's closed
 * so we can reopen with the last used value.
 *
 * Uses split state/dispatch contexts to optimize re-renders:
 * - Components that only read the open state won't re-render when only the setter is invoked
 * - Components that only toggle the panel never re-render, since the setter identity is stable
 */
export function FindInFileOpenProvider({children, isBlame}: {children: React.ReactNode; isBlame: boolean}) {
  const searchTerm = useSearchTerm()
  const setSearchTerm = useSetSearchTerm()
  const cursorEnabled = useIsCursorEnabled(isBlame)
  const lastSearchTerm = useRef<string>('')
  const [findInFileOpen, setRawFindInFileOpen] = useState<boolean>(false)

  // Track the latest search term in a ref so setFindInFileOpen can stay referentially stable. Without this, the
  // setter (and therefore the dispatch context value) would change on every keystroke, re-rendering every consumer.
  const searchTermRef = useRef(searchTerm)
  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])

  const setFindInFileOpen = useCallback(
    (open: boolean) => {
      const currentSearchTerm = searchTermRef.current
      if (open && currentSearchTerm === '' && lastSearchTerm.current !== '') {
        setSearchTerm(lastSearchTerm.current)
      } else if (!open && currentSearchTerm !== '') {
        lastSearchTerm.current = currentSearchTerm
        setSearchTerm('')
      }

      setRawFindInFileOpen(open)
    },
    [setSearchTerm],
  )

  return (
    <FindInFileOpenStateContext value={cursorEnabled ? false : findInFileOpen}>
      <FindInFileOpenDispatchContext value={setFindInFileOpen}>{children}</FindInFileOpenDispatchContext>
    </FindInFileOpenStateContext>
  )
}

/**
 * Hook to get the setter for the find-in-file panel.
 * Never re-renders due to open state or search term changes.
 */
export function useSetFindInFileOpen(): (open: boolean) => void {
  return use(FindInFileOpenDispatchContext)
}

/**
 * Hook to get both the open state and its setter.
 * Re-renders when the open state changes.
 * Use useSetFindInFileOpen() if you only need to toggle the panel.
 */
export function useFindInFileOpen(): {findInFileOpen: boolean; setFindInFileOpen: (open: boolean) => void} {
  const findInFileOpen = use(FindInFileOpenStateContext)
  const setFindInFileOpen = use(FindInFileOpenDispatchContext)

  return useMemo(() => ({findInFileOpen, setFindInFileOpen}), [findInFileOpen, setFindInFileOpen])
}
